import { FetchBaseQueryError } from '@reduxjs/toolkit/query'

import { getConceptsReadBack, getScreenshotsReadBack, sameScreenshotLists, toScreenshotItems } from '../features/analysis-screenshots/helpers/screenshotHelpers'
import { AnalysisScreenshotItem, ConceptMatchGroup, ConceptsReadBack, ScreenshotsReadBack } from '../features/analysis-screenshots/types/AnalysisScreenshots'
import { InternalAnalysis } from '../features/analysis/types/InternalAnalysis'
import { InternalCategoryFeature } from '../types/InternalCategory'
import { customError, optionalJsonOrText } from './helpers'
import { categoryFeaturesTag, internalApiserverApi } from './internal-apiserver'

export const screenshotsChangedError = 'SCREENSHOTS_CHANGED'

const analysisTag = (id: string) => [{ type: 'internalAnalysis' as const, id }]

// The concepts list the screenshots they're matched with, and Implementation Examples and Bulk Assign read the screenshots.
const conceptWriteTags = (analysisId: string, groups: ConceptMatchGroup[]) => [
  ...analysisTag(analysisId),
  'internalConcepts' as const,
  'internalBulkAssignScreenshots' as const,
  ...Array.from(new Set(groups.flatMap(({ screenshotsIds }) => screenshotsIds))).map((id) => ({ type: 'internalImplementationScreenshots' as const, id })),
]

// The writes' answers aren't used, and may be empty or plain text.
type BaseQuery = (arg: string | { url: string; method?: string; body?: unknown; responseHandler?: typeof optionalJsonOrText }) => Promise<{
  data?: unknown
  error?: FetchBaseQueryError
}>

// `analyses/{id}/refresh` is what every screen of the analysis reads.
const readStoredItems = async (baseQuery: BaseQuery, analysisId: string) => {
  const stored = await baseQuery(`analyses/${analysisId}/refresh`)
  if (stored.error) return { error: stored.error }
  return { items: toScreenshotItems((stored.data as InternalAnalysis | null)?.screenshots) }
}

export type SaveScreenshotsResult = { stored: AnalysisScreenshotItem[]; readBack: ScreenshotsReadBack }
export type DeleteScreenshotsResult = { stored: AnalysisScreenshotItem[]; stillListedIds: string[] }
export type ConceptsResult = { stored: AnalysisScreenshotItem[]; readBack: ConceptsReadBack }

const analysisScreenshotsApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['internalAnalysis'] }).injectEndpoints({
  endpoints: (builder) => ({
    // Every feature of the answered categories, inactive ones included, so every stored legacy id resolves.
    getAnalysisAnswerFeatures: builder.query<InternalCategoryFeature[], string[]>({
      async queryFn(categoryIds, _api, _extraOptions, baseQuery) {
        const answers = await Promise.all(categoryIds.map((categoryId) => baseQuery(`i/categories/${categoryId}/features`)))
        const failed = answers.find((answer) => answer.error)
        if (failed?.error) return { error: failed.error }
        return { data: answers.flatMap((answer) => (answer.data as InternalCategoryFeature[] | null) ?? []) }
      },
      providesTags: (_result, _error, categoryIds) => categoryIds.map(categoryFeaturesTag),
    }),
    // The `PUT` replaces the whole list, so nothing is written when the stored list isn't the one the page loaded.
    saveAnalysisScreenshots: builder.mutation<SaveScreenshotsResult, { analysisId: string; loaded: AnalysisScreenshotItem[]; body: AnalysisScreenshotItem[] }>({
      async queryFn({ analysisId, loaded, body }, _api, _extraOptions, baseQuery) {
        const current = await readStoredItems(baseQuery as BaseQuery, analysisId)
        if (current.error) return { error: current.error }
        if (!sameScreenshotLists(current.items ?? [], loaded)) return { error: customError(screenshotsChangedError) }
        const saved = await baseQuery({ url: `i/analyses/${analysisId}/screenshot`, method: 'PUT', body, responseHandler: optionalJsonOrText })
        if (saved.error) return { error: saved.error }
        const stored = await readStoredItems(baseQuery as BaseQuery, analysisId)
        if (stored.error) return { error: stored.error }
        return { data: { stored: stored.items ?? [], readBack: getScreenshotsReadBack(body, stored.items ?? []) } }
      },
      invalidatesTags: (_result, _error, { analysisId }) => analysisTag(analysisId),
    }),
    // Permanent.
    deleteAnalysisScreenshots: builder.mutation<DeleteScreenshotsResult, { analysisId: string; screenshotIds: string[] }>({
      async queryFn({ analysisId, screenshotIds }, _api, _extraOptions, baseQuery) {
        const deleted = await baseQuery({
          url: `i/analyses/${analysisId}/screenshot/delete`,
          method: 'POST',
          body: screenshotIds,
          responseHandler: optionalJsonOrText,
        })
        if (deleted.error) return { error: deleted.error }
        const stored = await readStoredItems(baseQuery as BaseQuery, analysisId)
        if (stored.error) return { error: stored.error }
        const storedIds = new Set((stored.items ?? []).map(({ screenshotId }) => screenshotId))
        return { data: { stored: stored.items ?? [], stillListedIds: screenshotIds.filter((id) => storedIds.has(id)) } }
      },
      invalidatesTags: (_result, _error, { analysisId }) => analysisTag(analysisId),
    }),
    // Changes the concepts' Implementation Examples. Stops at the first failed call.
    matchScreenshotConcepts: builder.mutation<ConceptsResult, { analysisId: string; groups: ConceptMatchGroup[] }>({
      async queryFn({ analysisId, groups }, _api, _extraOptions, baseQuery) {
        for (const group of groups) {
          const matched = await baseQuery({ url: 'i/concepts/screenshots/match', method: 'POST', body: group, responseHandler: optionalJsonOrText })
          if (matched.error) return { error: matched.error }
        }
        const stored = await readStoredItems(baseQuery as BaseQuery, analysisId)
        if (stored.error) return { error: stored.error }
        return { data: { stored: stored.items ?? [], readBack: getConceptsReadBack(groups, stored.items ?? [], true) } }
      },
      invalidatesTags: (_result, _error, { analysisId, groups }) => conceptWriteTags(analysisId, groups),
    }),
    unmatchScreenshotConcepts: builder.mutation<ConceptsResult, { analysisId: string; group: ConceptMatchGroup }>({
      async queryFn({ analysisId, group }, _api, _extraOptions, baseQuery) {
        const unmatched = await baseQuery({ url: 'i/concepts/screenshots/unmatch', method: 'POST', body: group, responseHandler: optionalJsonOrText })
        if (unmatched.error) return { error: unmatched.error }
        const stored = await readStoredItems(baseQuery as BaseQuery, analysisId)
        if (stored.error) return { error: stored.error }
        return { data: { stored: stored.items ?? [], readBack: getConceptsReadBack([group], stored.items ?? [], false) } }
      },
      invalidatesTags: (_result, _error, { analysisId, group }) => conceptWriteTags(analysisId, [group]),
    }),
  }),
})

export const {
  useGetAnalysisAnswerFeaturesQuery,
  useSaveAnalysisScreenshotsMutation,
  useDeleteAnalysisScreenshotsMutation,
  useMatchScreenshotConceptsMutation,
  useUnmatchScreenshotConceptsMutation,
} = analysisScreenshotsApi
